<template>
  <div
    v-if="visible"
    :class="wrapClass"
    :style="{ backgroundColor: bgColor || undefined, color: textColor || undefined }"
  >
    <div class="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-2.5">
      <p class="text-sm font-medium">
        {{ title }}
      </p>
      <div class="flex shrink-0 items-center gap-2">
        <UButton
          v-if="ctaLabel"
          :to="builderPreview ? undefined : ctaLink"
          size="xs"
          color="neutral"
          variant="soft"
        >
          {{ ctaLabel }}
        </UButton>
        <UButton
          v-if="dismissible && !builderPreview"
          icon="i-lucide-x"
          size="xs"
          color="neutral"
          variant="ghost"
          aria-label="بستن"
          @click="dismiss"
        />
      </div>
    </div>
  </div>

  <UModal
    v-if="style === 'popup' && !builderPreview"
    v-model:open="popupOpen"
  >
    <template #content>
      <div class="space-y-4 p-6 text-center">
        <p class="text-lg font-semibold">
          {{ title }}
        </p>
        <UButton
          v-if="ctaLabel"
          :to="ctaLink"
        >
          {{ ctaLabel }}
        </UButton>
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
const props = defineProps<{
  title?: string
  ctaLabel?: string
  ctaLink?: string
  style?: 'top-bar' | 'popup' | 'sticky-bar'
  dismissible?: boolean
  showFrom?: string
  showUntil?: string
  bgColor?: string
  textColor?: string
  builderPreview?: boolean
}>()

const STORAGE_KEY = 'aradcore:campaign-banner-dismissed'
const dismissed = ref(false)
const popupOpen = ref(false)

const inWindow = computed(() => {
  const now = Date.now()
  if (props.showFrom && now < new Date(props.showFrom).getTime()) return false
  if (props.showUntil && now > new Date(props.showUntil).getTime()) return false
  return true
})

const visible = computed(() => {
  if (props.builderPreview) return props.style !== 'popup'
  if (!inWindow.value || dismissed.value) return false
  return props.style !== 'popup'
})

const wrapClass = computed(() => {
  if (props.style === 'sticky-bar') return 'sticky top-0 z-40 border-b border-default/40'
  return 'border-b border-default/40'
})

function dismiss() {
  dismissed.value = true
  popupOpen.value = false
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()))
  }
  catch {
    // ignore
  }
}

onMounted(() => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw && Date.now() - Number(raw) < 24 * 60 * 60 * 1000) dismissed.value = true
  }
  catch {
    // ignore
  }
  if (props.builderPreview || props.style !== 'popup' || dismissed.value || !inWindow.value) return
  const onLeave = (event: MouseEvent) => {
    if (event.clientY <= 0) popupOpen.value = true
  }
  let lastY = 0
  const onScroll = () => {
    const y = window.scrollY
    if (y < lastY - 40 && y < 80) popupOpen.value = true
    lastY = y
  }
  document.addEventListener('mouseleave', onLeave)
  window.addEventListener('scroll', onScroll, { passive: true })
  onUnmounted(() => {
    document.removeEventListener('mouseleave', onLeave)
    window.removeEventListener('scroll', onScroll)
  })
})
</script>
